import React, { useCallback, useState } from "react";
import { BhiDialog } from 'src/UIKit/components/Dialog';
import {
  Button,
  DialogActions,
  DialogContent,
} from "@mui/material";

export type DialogButtonColors = "error" | "primary";
export type DialogButtonVariants = "outlined" | "contained" | "hidden";

type ConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  content: string;
  confirmButtonText?: string;
  confirmButtonColor: DialogButtonColors;
  cancelButtonColor: DialogButtonColors;
  cancelButtonVariant: DialogButtonVariants;
  confirmButtonVariant?: DialogButtonVariants;
};

type DialogType = {
  title: string;
  content: string;
  onConfirm: () => void;
  confirmButtonText?: string;
  confirmButtonColor?: DialogButtonColors;
  cancelButtonColor?: DialogButtonColors;
  cancelButtonVariant?: DialogButtonVariants;
  confirmButtonVariant?: DialogButtonVariants;
};

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  confirmButtonText = "Confirm",
  confirmButtonColor = "error",
  cancelButtonColor = "primary",
  cancelButtonVariant = "contained",
  confirmButtonVariant = "contained",
}: ConfirmationDialogProps) => {
  return (
    <BhiDialog open={open} onClose={onClose} style={{padding: "10px"}} title={title}>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        {cancelButtonVariant !== "hidden" && (
          <Button
            onClick={onClose}
            variant={cancelButtonVariant}
            color={cancelButtonColor}
          >
            Cancel
          </Button>
        )}
        {confirmButtonVariant !== "hidden" && (
          <Button
            autoFocus
            onClick={onConfirm}
            variant={confirmButtonVariant}
            color={confirmButtonColor}
          >
            {confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </BhiDialog>
  );
};

const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState("Confirm");
  const [dialogConfig, setDialogConfig] = useState<{
    title: string;
    content: string;
    onConfirm: () => void;
    confirmButtonColor?: DialogButtonColors;
    cancelButtonColor?: DialogButtonColors;
    cancelButtonVariant?: DialogButtonVariants;
    confirmButtonVariant?: DialogButtonVariants;
  }>({
    title: "",
    content: "",
    onConfirm: () => {},
    confirmButtonColor: "error",
    cancelButtonColor: "primary",
    cancelButtonVariant: "contained",
    confirmButtonVariant: "contained",
  });

  const openDialog = useCallback(({
    title,
    content,
    onConfirm,
    confirmButtonText,
    confirmButtonColor,
    cancelButtonColor,
    cancelButtonVariant,
    confirmButtonVariant
  }: DialogType) => {
    if (confirmButtonText) {
      setConfirmButtonText(confirmButtonText);
    }
    setDialogConfig({ title, content, onConfirm, confirmButtonColor, cancelButtonColor, cancelButtonVariant, confirmButtonVariant });
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  const Dialog = useCallback(
    () => (
      <ConfirmationDialog
        open={isOpen}
        onClose={closeDialog}
        onConfirm={() => {
          dialogConfig.onConfirm();
          closeDialog();
        }}
        title={dialogConfig.title}
        content={dialogConfig.content}
        confirmButtonText={confirmButtonText}
        confirmButtonColor={dialogConfig.confirmButtonColor || "error"}
        cancelButtonColor={dialogConfig.cancelButtonColor || "primary"}
        cancelButtonVariant={dialogConfig.cancelButtonVariant || "contained"}
        confirmButtonVariant={dialogConfig.confirmButtonVariant || "contained"}
      />
    ),
    [isOpen, dialogConfig, confirmButtonText]
  );

  return {
    openDialog,
    ConfirmationDialog: Dialog,
  };
};

export default useConfirmationDialog;
